/**
 * Shared Inherent/Residual Risk scoring, extracted from RiskDetail.tsx so ControlDetail.tsx can
 * reuse the identical formula to recalculate every Risk linked to a Control right after that
 * Control is saved. Before this, residual_risk only ever recomputed when a user opened that
 * specific Risk's own Detail page — a Control's maturity/evidence/effectiveness changing on its
 * own Detail page never propagated to any Risk that depended on it, silently going stale.
 */
import { riskApi, linkedArtefactsApi, controlApi, assetsApi } from '../services/api';
import { parseEffectivenessScore, maturityToScore, isImplementationComplete } from './controlConformity';

export interface ControlLike {
  fields?: Record<string, any>;
}

const normalizeOneToFive = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const text = String(value ?? '').trim();
  if (!text) return 0;
  const numeric = Number(text);
  if (Number.isFinite(numeric)) return numeric;
  const match = text.match(/\b([1-5])\b/);
  if (match) return Number(match[1]);
  return 0;
};

export const mapLikelihoodToNumber = (value: unknown): number => {
  const direct = normalizeOneToFive(value);
  if (direct >= 1 && direct <= 5) return direct;
  const map: Record<string, number> = {
    rare: 1, unlikely: 2, possible: 3, likely: 4, 'almost certain': 5,
    'very low': 1, low: 2, medium: 3, high: 4, 'very high': 5,
  };
  return map[String(value ?? '').toLowerCase()] || 0;
};

export const mapImpactToNumber = (value: unknown): number => {
  const direct = normalizeOneToFive(value);
  if (direct >= 1 && direct <= 5) return direct;
  const map: Record<string, number> = {
    insignificant: 1, negligible: 1, minor: 2, moderate: 3, major: 4, catastrophic: 5, severe: 5,
    'very low': 1, low: 2, medium: 3, high: 4, 'very high': 5,
  };
  return map[String(value ?? '').toLowerCase()] || 0;
};

export const categoryFromScore = (score: number): string => {
  if (score >= 21) return 'Critical';
  if (score >= 16) return 'High';
  if (score >= 11) return 'Medium';
  if (score >= 6) return 'Low';
  if (score >= 1) return 'Minimal';
  return '';
};

const ASSET_CRITICALITY_MULTIPLIER: Record<string, number> = {
  critical: 1.2, high: 1.1, medium: 1.0, low: 0.9,
};
export const assetCriticalityMultiplier = (value: unknown): number =>
  ASSET_CRITICALITY_MULTIPLIER[String(value ?? '').toLowerCase()] ?? 1.0;

const CONTROL_EFFECTIVENESS_CAP = 30;
const LIKELIHOOD_REDUCING_CONTROL_TYPES = new Set(['preventive', 'deterrent']);
const IMPACT_REDUCING_CONTROL_TYPES = new Set(['detective', 'corrective', 'compensating']);

// See RUB-50672: a control rolled out during a months-long implementation programme has real
// signal (current_maturity, implementation_status) long before anyone opens its own Detail page
// to run the full evidence/testing-backed effectivenessBreakdown and save a real
// control_effectiveness score. Estimates from maturity alone, at the same 50% weight
// ControlDetail.tsx's real 3-factor formula gives maturity, capped lower again when
// implementation isn't complete. A real, saved score always wins over this estimate; a control
// marked Not Applicable is excluded entirely rather than defaulted.
export const assumedControlEffectiveness = (control: ControlLike): number => {
  const implStatusRaw = String(control?.fields?.['implementation_status'] || '');
  if (implStatusRaw.toLowerCase().includes('not applicable')) return 0;
  const maturityNum = maturityToScore(control?.fields?.['current_maturity']) ?? 0;
  let estimate = (maturityNum / 5) * 100 * 0.5;
  if (!isImplementationComplete(implStatusRaw)) estimate = Math.min(estimate, 20);
  return Math.round(estimate);
};

/** Combine a list of capped per-control effectiveness percentages (0-100) into one reduction
 * fraction (0-1) via multiplicative diminishing returns. */
const combineControlReductions = (percentages: number[]): number => {
  let survivingFraction = 1;
  for (const pct of percentages) {
    survivingFraction *= 1 - Math.min(pct, CONTROL_EFFECTIVENESS_CAP) / 100;
  }
  return 1 - survivingFraction;
};

export interface RiskScores {
  inherentRisk: string;
  residualRisk: string;
  residualIsAssumed: boolean;
}

/** Inverse of computeRiskScores' `${category} (${score})` display strings — e.g. "High (16)" ->
 * 16. Null (not 0) when there's no score to find, so a caller can tell "genuinely never
 * computed" apart from a real low score and fall back to something cruder only in that case. */
export const parseRiskScore = (raw: unknown): number | null => {
  if (typeof raw !== 'string') return null;
  const match = raw.match(/\((\d+)\)/);
  if (!match) return null;
  const score = parseInt(match[1], 10);
  return Number.isFinite(score) ? score : null;
};

/** Pure computation, no I/O — identical to what RiskDetail.tsx ran inline before RUB-50672,
 * now shared so ControlDetail.tsx's post-save cascade produces the exact same numbers a user
 * would see by opening the Risk itself. */
export const computeRiskScores = (
  likelihood: unknown,
  impact: unknown,
  linkedAssetCriticality: unknown,
  linkedControls: ControlLike[]
): RiskScores => {
  const likelihoodScore = mapLikelihoodToNumber(likelihood);
  const impactScore = mapImpactToNumber(impact);
  const criticalityMultiplier = assetCriticalityMultiplier(linkedAssetCriticality);

  if (likelihoodScore <= 0 || impactScore <= 0) {
    return { inherentRisk: '', residualRisk: '', residualIsAssumed: false };
  }

  const inherentScore = Math.min(25, Math.round(likelihoodScore * impactScore * criticalityMultiplier));
  const inherentCategory = categoryFromScore(inherentScore);
  const inherentRisk = inherentCategory ? `${inherentCategory} (${inherentScore})` : '';

  const likelihoodReducingPcts: number[] = [];
  const impactReducingPcts: number[] = [];
  const wholeScoreReducingPcts: number[] = [];
  let residualIsAssumed = false;

  (linkedControls || []).forEach((control) => {
    const raw = control?.fields?.control_effectiveness || control?.fields?.effectiveness || '';
    const parsed = parseEffectivenessScore(raw);
    const pct = parsed !== null ? parsed : assumedControlEffectiveness(control);
    if (pct <= 0) return;
    if (parsed === null) residualIsAssumed = true;
    const controlType = String(control?.fields?.control_type || '').trim().toLowerCase();
    if (LIKELIHOOD_REDUCING_CONTROL_TYPES.has(controlType)) likelihoodReducingPcts.push(pct);
    else if (IMPACT_REDUCING_CONTROL_TYPES.has(controlType)) impactReducingPcts.push(pct);
    else wholeScoreReducingPcts.push(pct);
  });

  const likelihoodReduction = combineControlReductions(likelihoodReducingPcts);
  const impactReduction = combineControlReductions(impactReducingPcts);
  const wholeScoreReduction = combineControlReductions(wholeScoreReducingPcts);

  const residualLikelihood = likelihoodScore * (1 - likelihoodReduction);
  const residualImpact = impactScore * (1 - impactReduction);
  const residualRaw = residualLikelihood * residualImpact * criticalityMultiplier * (1 - wholeScoreReduction);
  // A risk is never fully eliminated by its controls -- floor at the bottom of the "Minimal"
  // band rather than letting heavy reduction round all the way down to no category.
  const residualScore = Math.max(1, Math.min(25, Math.round(residualRaw)));
  const residualCategory = categoryFromScore(residualScore);
  const residualRisk = residualCategory ? `${residualCategory} (${residualScore})` : '';

  return { inherentRisk, residualRisk, residualIsAssumed };
};

const fetchLinkedControls = async (riskId: string): Promise<ControlLike[]> => {
  const linksResponse: any = await linkedArtefactsApi.getLinks(riskId, 'risk');
  const controlIds: string[] = (linksResponse?.links || [])
    .filter((l: any) => l?.artefact_type === 'control')
    .map((l: any) => l.artefact_id)
    .filter(Boolean);
  if (controlIds.length === 0) return [];
  const controls = await Promise.all(controlIds.map((id) => controlApi.getOne(id).catch(() => null)));
  return controls.filter(Boolean) as ControlLike[];
};

/**
 * Recomputes one Risk's inherent/residual scores from its current likelihood/impact/linked
 * asset/linked controls, and persists them if they've changed. Safe to call any time a Risk's
 * inputs might have moved without that Risk's own Detail page being open. Sends the FULL current
 * `fields` object back with just the two calculated values changed — NOT only the two changed
 * fields: update_risk's required-field check (prod-nimbus-risks) runs against whatever `fields`
 * is IN THE REQUEST BODY, before its own later merge into the stored record, so a partial
 * payload missing an already-set required field (e.g. Risk Title) gets rejected outright as
 * "removing" it, 400, even though the merge would never have actually removed anything. Returns
 * whether it actually wrote a change, so a caller recalculating many Risks at once can report a
 * meaningful summary.
 */
export const recalculateRiskScores = async (riskId: string): Promise<boolean> => {
  const risk: any = await riskApi.getOne(riskId);
  if (!risk) return false;
  const fields = risk.fields || {};

  const linkedAssetId = typeof fields.linked_asset === 'string' ? fields.linked_asset : '';
  let linkedAssetCriticality: unknown;
  if (linkedAssetId) {
    const assetDetail: any = await assetsApi.getOne(linkedAssetId).catch(() => null);
    linkedAssetCriticality = (assetDetail?.asset ?? assetDetail)?.criticality;
  }

  const linkedControls = await fetchLinkedControls(riskId);
  const { inherentRisk, residualRisk } = computeRiskScores(
    fields.likelihood,
    fields.impact,
    linkedAssetCriticality,
    linkedControls
  );

  if (fields.inherent_risk === inherentRisk && fields.residual_risk === residualRisk) return false;

  await riskApi.update(riskId, {
    fields: { ...fields, inherent_risk: inherentRisk, residual_risk: residualRisk },
  });
  return true;
};

/**
 * Called after a Control save succeeds: refreshes every Risk that links to it, so a Control
 * becoming more (or less) mature/implemented/effective is reflected on those Risks' persisted
 * residual score without anyone needing to open each Risk individually. Best-effort — one
 * failing Risk (e.g. deleted mid-flight) doesn't stop the others, and a failure here never blocks
 * or reverts the Control save itself.
 */
export const recalculateRisksLinkedToControl = async (controlId: string): Promise<void> => {
  const linksResponse: any = await linkedArtefactsApi.getLinks(controlId, 'control').catch(() => null);
  const riskIds: string[] = (linksResponse?.links || [])
    .filter((l: any) => l?.artefact_type === 'risk')
    .map((l: any) => l.artefact_id)
    .filter(Boolean);

  await Promise.all(
    riskIds.map((riskId) =>
      recalculateRiskScores(riskId).catch((error) => {
        console.error(`Failed to recalculate residual risk for ${riskId} after control ${controlId} save:`, error);
      })
    )
  );
};

export interface RecalculateAllResult {
  total: number;
  updated: number;
  failed: number;
}

/**
 * One-time (or occasional, manually-triggered) catch-up for every existing Risk — walks the
 * whole register rather than waiting for each linked Control to be individually re-saved.
 * Needed because recalculateRisksLinkedToControl above only fires going forward, from the
 * moment RUB-50672 shipped; a Risk whose linked Control hasn't been touched since then is still
 * showing whatever was persisted before this feature existed. Exposed as an admin action
 * (SettingsPage.tsx) rather than a script, so it runs as a real logged-in user — the backend's
 * audit log (log_audit in prod-nimbus-risks) attributes each write to whoever clicked it, same
 * as any other save, instead of an untraceable direct data change.
 */
export const recalculateAllRiskScores = async (): Promise<RecalculateAllResult> => {
  const risks: any[] = await riskApi.listAll();
  let updated = 0;
  let failed = 0;
  await Promise.all(
    risks.map(async (risk) => {
      const riskId = risk?.riskId;
      if (!riskId) return;
      try {
        if (await recalculateRiskScores(riskId)) updated += 1;
      } catch (error) {
        failed += 1;
        console.error(`Failed to recalculate risk ${riskId}:`, error);
      }
    })
  );
  return { total: risks.length, updated, failed };
};
